import {asText} from '@prismicio/client'
import {PrismicRichText, SliceZone} from '@prismicio/react'
import type {GlossaryPageDocument, GlossaryTermDocument, NavigationDocumentData} from 'prismicio-types'
import {useMemo} from 'react'
import {components} from 'slices'
import {Layout} from 'src/common/Layout'
import {Meta} from 'src/common/Meta'
import {getMetaProps} from 'src/common/utils/documentHelpers'
import {AlphabetNavigation} from './AlphabetNavigation'
import {TermList} from './TermList'

export interface GlossaryProps {
  page: GlossaryPageDocument
  glossaryTerms: Array<GlossaryTermDocument>
  navigationData: NavigationDocumentData
}

export function Glossary(props: GlossaryProps) {
  const {page, glossaryTerms, navigationData} = props

  // Group terms by first letter
  const groupedTerms = useMemo(
    () =>
      glossaryTerms.reduce(
        (acc, term) => {
          const termAsText = asText(term.data.term)
          const firstLetter = termAsText?.[0]?.toUpperCase() || '#'

          if (!acc[firstLetter]) {
            acc[firstLetter] = []
          }

          acc[firstLetter].push(term)
          return acc
        },
        {} as Record<string, Array<GlossaryTermDocument>>,
      ),
    [glossaryTerms],
  )

  // Get set of available letters
  const availableLetters = useMemo(() => new Set(Object.keys(groupedTerms)), [groupedTerms])

  return (
    <Layout
      alternateLanguages={page.alternate_languages}
      navigationData={navigationData}
      pageUid={page.uid}
      locale={page.lang}
    >
      <Meta {...getMetaProps(page)} />

      <div className="relative mx-auto w-full max-w-[1200px] px-5 md:px-10 lg:px-5 xl:px-0">
        {/* Hero/Intro Section */}
        <section className="flex flex-col border-b border-b-moss-grey-100 pb-12 pt-24 lg:pb-20 lg:pt-[137px]">
          <div className="col-[inner] max-w-4xl">
            {page.data.title && (
              <PrismicRichText
                field={page.data.title}
                components={{
                  heading1: ({children}) => (
                    <h1 className="text-36 font-medium leading-normal tracking-[-0.36px] text-moss-black lg:text-48 lg:tracking-[-0.48px]">
                      {children}
                    </h1>
                  ),
                }}
              />
            )}
            {page.data.description && (
              <PrismicRichText
                field={page.data.description}
                components={{
                  paragraph: ({children}) => <p className="prose prose-lg max-w-none text-666563">{children}</p>,
                }}
              />
            )}
          </div>
        </section>

        {/* Desktop: Horizontal sticky alphabet navigation */}
        <AlphabetNavigation
          variant="desktop"
          availableLetters={availableLetters}
          navigationAriaLabel={page.data.navigation_aria_label}
        />

        {/* Glossary Content Section */}
        <section className="relative flex flex-col py-12 md:py-16 lg:py-20">
          {/* Mobile: Vertical sidebar alphabet navigation */}
          <AlphabetNavigation
            variant="mobile"
            availableLetters={availableLetters}
            navigationAriaLabel={page.data.navigation_aria_label}
          />

          {/* Terms List */}
          <div className="w-full">
            <TermList groupedTerms={groupedTerms} />
          </div>
        </section>
      </div>

      {/* Footer CTA Slices */}
      {page.data.slices && page.data.slices.length > 0 && (
        <SliceZone components={components} slices={page.data.slices} context={{pageUid: page.uid}} />
      )}
    </Layout>
  )
}
